import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import { BaseUrl } from "../../helper/BaseUrl";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const newsEditEndpoint = ApiEndpoints.news.newsEdit;
const newsUpdateEndpoint = ApiEndpoints.news.newsUpdate;

export default function NewsEdit() {
  const navigate = useNavigate();
  const { media_id } = useParams();

  const [previews, setPreviews] = useState({ banner: null, thumbnail: null,});

  const [postData, setPostData] = useState({title: "", slug: "", author_id: "", date: new Date(), description: "", banner: null, alt_banner: "", thumbnail: null,  alt_thumbnail: "",});

  const [authors, setAuthors] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest("GET", `${newsEditEndpoint}${media_id}`,);
        setPostData(response.media);
        setAuthors(response.authors);
        setPreviews((prev) => ({
          ...prev,
          banner: BaseUrl + response.media.banner || prev.banner,
          thumbnail: BaseUrl + response.media.thumbnail || prev.thumbnail,
        }));

      } catch (error) {
        console.error("Error fetching news data:", error);
      }
    };

    fetchData();
  }, [media_id]);


  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];

      // Update the previews state
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      // Update the postData state
      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      let updatedValue = value;
      if (name === "date") {
        updatedValue = new Date(value).toISOString();
      }
      setPostData((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const [key, value] of Object.entries(postData)) {
      if (value === "" || value === null) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      formData.append(key, value);
    }); 

    try {
      const response = await ApiRequest("POST", newsUpdateEndpoint + media_id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(response.message);
      navigate("/news");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const pagename = {
    name: 'News Management',
    user: 'Admin',
    innerpage: 'News Management / News / Edit News'
  }

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">
        <form id="newsEdit" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>title<small>*</small></label>
                  </div>
                  <input value={postData.title} onChange={handleChange} name="title" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input value={postData.slug} onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Author Name<small>*</small></label>
                  </div>
                  <CustomSelect onChange={handleChange} defaultValue={postData?.author_id} name="author_id" options={authors} placeholder="Select Author Name" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Date<small>*</small></label>
                  </div>
                  <input value={postData.date ? new Date(postData.date).toISOString().split("T")[0] : ""} onChange={handleChange} name="date" type="date" className="form-control" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description} onChange={(event, editor) => handleEditorChange("description", editor.getData())} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Banner Image (1608 × 680)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="banner"
                    acceptedFormats={[".webp"]} 
                    onImageChange={handleChange}
                    requiredWidth={1608}
                    requiredHeight={680}
                    imagePreview={previews.banner}
                    />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Banner Image Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.alt_banner} name="alt_banner" type="text" className="form-control" placeholder="Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image (508 × 330)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    acceptedFormats={[".webp"]} 
                    onImageChange={handleChange}
                    requiredWidth={508}
                    requiredHeight={330}
                    imagePreview={previews.thumbnail}/>
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input onChange={handleChange} value={postData.alt_thumbnail} name="alt_thumbnail" type="text" className="form-control" placeholder="Alt Tag" />
                </div>
              </div>
            </div>
          </div>
        </form>

        <button type="submit" form="newsEdit" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
